import {
  Button,
  CheckboxGroup,
  CheckboxGroupItem,
  CheckboxGroupSection,
  Col,
  IconButton,
  MultiSelect,
  P,
  Radio,
  RadioGroup,
  Row,
  Stack,
  TextField,
} from '@workramp/ui';
import moment from 'moment';
import StringUtils from '~/common/string-utils';
import { capitalize } from '~/utils';
import { useDebouncedValue, useQueryParam } from '~/hooks';
import { APP_CONSTANTS, I18n, getExternalUser } from '~/common/explicit-exports';
import { getSpaceForCurrentLanguage } from '~/common/localization-utils';
import {
  LearnerAIQuestionAnswer,
  StyledAdvancedSearchStack,
} from '~/elc/features/learner-search/components/LearnerAIQuestionAnswer';
import { useAIAnswerWithSubscription } from '~/elc/features/learner-search/hooks/useAIAnswerWithSubscription';
import { ResultsTable } from '../ResultsTable';
import { createAcademyAISearchAnswer, useAlgoliaHubSearch } from '../../api';
import { StyledContainer, StyledNoLeftPaddingCol } from './DedicatedLearnerSearchPage.styles';

const TRANSLATED_SEARCH_RESULTS_FOR = I18n.t('js.admin.universities.search_results_for', {
  space: getSpaceForCurrentLanguage(),
});
const TRANSLATED_RELEVANCE = I18n.t('js.learner.advanced_search.sort.relevance');
const TRANSLATED_RECENT = I18n.t('js.learner.advanced_search.sort.recent');
const TRANSLATED_LAST_7_DAYS = I18n.t('js.learner.advanced_search.filters.last_7_days');
const TRANSLATED_LAST_30_DAYS = I18n.t('js.learner.advanced_search.filters.last_30_days');
const TRANSLATED_LAST_YEAR = I18n.t('js.learner.advanced_search.filters.last_year');
const TRANSLATED_ALL_TIME = I18n.t('js.learner.advanced_search.filters.all_time');
const TRANSLATED_TYPE = I18n.t('js.learner.advanced_search.filters.type');

const SORT_OPTIONS = [TRANSLATED_RELEVANCE, TRANSLATED_RECENT] as const;
const SORT_FILTERS = SORT_OPTIONS.map((o) => ({
  label: capitalize(o),
  value: o,
}));
type SortTypes = (typeof SORT_OPTIONS)[number];

const UPDATED_FILTER_VALUES = ['0', '7', '30', '365'];
const UPDATED_FILTERS_TO_LABEL = {
  '7': TRANSLATED_LAST_7_DAYS,
  '30': TRANSLATED_LAST_30_DAYS,
  '365': TRANSLATED_LAST_YEAR,
  '0': TRANSLATED_ALL_TIME,
};
type UpdatedAtFilterTypes = (typeof UPDATED_FILTER_VALUES)[number];

const SEARCH_PARAM_OPTIONS = {
  QUERY: 'query',
  TYPE: 'type',
  UPDATED_DATE: 'updatedDate',
  SORT_BY: 'sortBy',
};

const COURSE_TYPES: string[] = [APP_CONSTANTS.ASSET_PICKER_TYPES.GUIDE, APP_CONSTANTS.ASSET_PICKER_TYPES.SCORM];

const ALGOLIA_SEARCH_DEBOUNCE_MS = 300;

export function DedicatedLearnerSearchPage({
  originProduct,
  initialSearchTerm,
  algoliaSearchApiKey,
  algoliaAppId,
  academySearchableContentTypes,
  academyContentTypesToLocalizedString,
  communityContentTypesToLocalizedString,
  aiSearchAnswersEnabled,
}: {
  originProduct: 'academy' | 'community';
  initialSearchTerm: string;
  algoliaAppId: string;
  algoliaSearchApiKey: string;
  academySearchableContentTypes: string[];
  academyContentTypesToLocalizedString: Record<string, string>;
  communityContentTypesToLocalizedString: Record<string, string>;
  aiSearchAnswersEnabled?: boolean;
}) {
  const TRANSLATED_BACK_TO_PRODUCT = I18n.t('common.academy.back_to_academy', {
    academy_title: originProduct === 'academy' ? 'Academy' : 'Community',
  });
  const communitySearchableContentTypes = Object.keys(communityContentTypesToLocalizedString);

  const userId = getExternalUser()?.id;
  const [query, setQuery] = useQueryParam<string>(SEARCH_PARAM_OPTIONS.QUERY, initialSearchTerm);
  const debouncedAIAnswerQuery = useDebouncedValue(query, 1000);

  // Use the AI Answer hook with Academy configuration
  const {
    loading: answerLoading,
    answer,
    content: answerContent,
    progressMessage,
  } = useAIAnswerWithSubscription({
    question: debouncedAIAnswerQuery,
    userId,
    apiCall: createAcademyAISearchAnswer,
    enabled: aiSearchAnswersEnabled && !!userId,
    channelParams: { for_academy: true },
    minQuestionLength: 5,
  });
  const [selectedTypes, setSelectedTypes] = useQueryParam<string[]>(SEARCH_PARAM_OPTIONS.TYPE, []);
  const [updatedAtFilter, setUpdatedAtFilter] = useQueryParam<UpdatedAtFilterTypes>(
    SEARCH_PARAM_OPTIONS.UPDATED_DATE,
    '0',
  );
  const [sortFilter, setSortFilter] = useQueryParam<SortTypes>(SEARCH_PARAM_OPTIONS.SORT_BY, SORT_OPTIONS[0]);
  const params: { filters?: string; numericFilters?: string[] } = {};
  if (selectedTypes.length > 0) {
    let typeFilters = [...selectedTypes];
    let contentTypeFilters = [];

    if (selectedTypes.includes(APP_CONSTANTS.ASSET_PICKER_TYPES.GUIDE)) {
      typeFilters = typeFilters.filter((t) => !COURSE_TYPES.includes(t));
      contentTypeFilters = [...COURSE_TYPES];
    }

    if (selectedTypes.includes(APP_CONSTANTS.ASSET_PICKER_TYPES.RESOURCE)) {
      typeFilters.filter((t) => t !== APP_CONSTANTS.ASSET_PICKER_TYPES.RESOURCE);
      contentTypeFilters = [...contentTypeFilters, APP_CONSTANTS.ASSET_PICKER_TYPES.RESOURCE];
    }

    params.filters = typeFilters.map((t) => `type:${t}`).join(' OR ');

    if (contentTypeFilters.length > 0) {
      params.filters = `${contentTypeFilters.map((ct) => `content_type:${ct}`).join(' OR ')}${
        params.filters ? ` OR ${params.filters}` : ''
      }`;
    }
  }

  if (updatedAtFilter !== '0') {
    params.numericFilters = [
      `updated_at > ${moment(Date.now()).startOf('day').subtract(updatedAtFilter, 'days').format('x')}`,
    ];
  }

  const debouncedSearchQuery = useDebouncedValue(query, ALGOLIA_SEARCH_DEBOUNCE_MS, setQuery);
  const { data } = useAlgoliaHubSearch({
    algoliaAppId,
    algoliaSearchApiKey,
    value: debouncedSearchQuery,
    params,
  });

  const showAIAnswer = aiSearchAnswersEnabled && userId && (debouncedAIAnswerQuery?.length || 0) > 5;

  // Just so we can verify results for now
  const sortedData = sortFilter === 'relevance' ? data : [...(data || [])].sort((a, b) => b.updated_at - a.updated_at);

  const returnToProduct = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = window.location.pathname;
    }
    return null;
  };

  return (
    <StyledContainer>
      <Stack size={4}>
        <Row>
          <StyledNoLeftPaddingCol>
            <Button
              variant="text"
              color="secondary"
              leadingIconName="arrow-left:regular"
              onPress={() => returnToProduct()}
            >
              {TRANSLATED_BACK_TO_PRODUCT}
            </Button>
          </StyledNoLeftPaddingCol>
        </Row>
        {showAIAnswer && (
          <Row>
            <StyledAdvancedSearchStack size={2}>
              <LearnerAIQuestionAnswer
                loading={answerLoading}
                answer={answer}
                content={answerContent}
                progressMessage={progressMessage}
                variant="advanced"
                isAcademy
              />
            </StyledAdvancedSearchStack>
          </Row>
        )}
        <Row>
          <Col>
            <P size={3} weight="semibold">
              {`${TRANSLATED_SEARCH_RESULTS_FOR}"${query}"`}
            </P>
          </Col>
        </Row>
        <Row>
          <Col>
            <TextField
              label=""
              placeholder="Search"
              value={query}
              onChange={(t) => setQuery(t)}
              leadingIconName="search:regular"
              trailingButtonEl={
                <IconButton
                  label="Clear Search Term"
                  size="sm"
                  variant="text"
                  color="secondary"
                  iconName="x-small:regular"
                  onPress={() => setQuery('')}
                />
              }
              showTrailingButton={query?.length > 0}
            />
          </Col>
        </Row>
        <Row>
          <Col xs="auto">
            <MultiSelect
              label={TRANSLATED_TYPE}
              numSelections={selectedTypes.length}
              title={TRANSLATED_TYPE}
              width={350}
              onClear={() => setSelectedTypes([])}
            >
              <CheckboxGroup label="" value={selectedTypes} onChange={setSelectedTypes}>
                <CheckboxGroupSection title="Academy">
                  {academySearchableContentTypes.map((a) => (
                    <CheckboxGroupItem key={a} value={a}>
                      {academyContentTypesToLocalizedString[a]}
                    </CheckboxGroupItem>
                  ))}
                </CheckboxGroupSection>
                {communitySearchableContentTypes.length > 0 && (
                  <CheckboxGroupSection title="Community">
                    {communitySearchableContentTypes.map((c) => (
                      <CheckboxGroupItem key={c} value={c}>
                        {StringUtils.getCapitalizedWordsFromUnderscoredString(
                          communityContentTypesToLocalizedString[c],
                        )}
                      </CheckboxGroupItem>
                    ))}
                  </CheckboxGroupSection>
                )}
              </CheckboxGroup>
            </MultiSelect>
          </Col>
          <Col xs="fill">
            <MultiSelect
              label={UPDATED_FILTERS_TO_LABEL[updatedAtFilter]}
              numSelections={0}
              title="Updated"
              width={350}
              onClear={() => setUpdatedAtFilter(null)}
            >
              <RadioGroup value={updatedAtFilter} onChange={(f: UpdatedAtFilterTypes) => setUpdatedAtFilter(f)}>
                {Object.entries(UPDATED_FILTERS_TO_LABEL).map(([value, label]) => (
                  <Radio key={label} value={value}>
                    {label}
                  </Radio>
                ))}
              </RadioGroup>
            </MultiSelect>
          </Col>
          <Col xs="auto">
            <MultiSelect title="Relevance" label={capitalize(sortFilter)} onClear={() => setSortFilter('relevance')}>
              <RadioGroup value={sortFilter} onChange={(f: SortTypes) => setSortFilter(f)}>
                {SORT_FILTERS.map((sf) => (
                  <Radio key={sf.label} value={sf.value}>
                    {sf.label}
                  </Radio>
                ))}
              </RadioGroup>
            </MultiSelect>
          </Col>
        </Row>
        <Row>
          <ResultsTable
            results={sortedData}
            academyContentTypesToLocalizedString={academyContentTypesToLocalizedString}
          />
        </Row>
      </Stack>
    </StyledContainer>
  );
}
